import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { apiFetch, onSessionLost, refreshAccessToken, setAccessToken } from '@/api/client';
import type { LoginResponse, Me, TokenResponse } from '@/api/types';

import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type LoginResult,
} from './auth-context';

const SESSION_MESSAGES: Record<string, string> = {
  SESSION_EXPIRED: 'Votre session a expiré après une période d’inactivité. Reconnectez-vous.',
  SESSION_INVALID: 'Votre session a été fermée. Reconnectez-vous.',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<Me | null>(null);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);

  const reset = useCallback(
    (message: string | null) => {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
      setSessionMessage(message);
      queryClient.clear();
    },
    [queryClient],
  );

  const reloadUser = useCallback(async () => {
    const me = await apiFetch<Me>('/auth/me');
    setUser(me);
    setStatus('authenticated');
    return me;
  }, []);

  // Au chargement : une session existe-t-elle (cookie httpOnly) ?
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await refreshAccessToken();
      if (cancelled) return;
      if (!ok) {
        setStatus('anonymous');
        return;
      }
      try {
        await reloadUser();
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadUser]);

  useEffect(() => {
    onSessionLost((code) => reset(SESSION_MESSAGES[code] ?? SESSION_MESSAGES.SESSION_INVALID!));
    return () => onSessionLost(null);
  }, [reset]);

  const applyTokens = useCallback((tokens: TokenResponse) => {
    setAccessToken(tokens.access);
    setUser(tokens.user);
    setSessionMessage(null);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResult> => {
      const result = await apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { username, password },
        noRetry: true,
      });
      if (result.mfa_required && result.mfa_token) {
        return { status: 'mfa', mfaToken: result.mfa_token };
      }
      applyTokens(result as TokenResponse);
      return { status: 'done' };
    },
    [applyTokens],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string, remember = false) => {
      const tokens = await apiFetch<TokenResponse>('/auth/mfa/verify', {
        method: 'POST',
        body: { mfa_token: mfaToken, code, remember_device: remember },
        noRetry: true,
      });
      applyTokens(tokens);
    },
    [applyTokens],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST', noRetry: true });
    } finally {
      reset(null);
    }
  }, [reset]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, sessionMessage, login, verifyMfa, logout, reloadUser }),
    [status, user, sessionMessage, login, verifyMfa, logout, reloadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
